import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import MainHeader from "../components/MainHeader";
import MainFooter from "../components/MainFooter";
import Marquee from "../components/Marquee";
import { ArrowUpRight, Clapperboard, Ticket, Film } from "lucide-react";
import { useFilmmakerScope } from "../context/FilmmakerScope";

export default function Home() {
  const [nowShowing, setNowShowing] = useState([]);
  const [comingSoon, setComingSoon] = useState([]);
  const scope = useFilmmakerScope();

  useEffect(() => {
    document.body.classList.remove("theme-mini");
    if (scope.loading) return;
    if (scope.isFilmmakerMode && scope.slug) {
      api.get(`/filmmakers/${scope.slug}`).then(({ data }) => {
        const films = data.films || [];
        setNowShowing(films.filter((f) => f.is_now_showing));
        setComingSoon(films.filter((f) => f.coming_soon));
      });
    } else {
      api.get("/films/now-showing").then(({ data }) => setNowShowing(data));
      api.get("/films/coming-soon").then(({ data }) => setComingSoon(data));
    }
  }, [scope.loading, scope.isFilmmakerMode, scope.slug]);

  return (
    <div data-testid="home-page" className="bg-gs-cream min-h-screen">
      <MainHeader />

      {/* HERO */}
      <section className="relative max-w-7xl mx-auto px-6 md:px-12 pt-12 md:pt-20 pb-12">
        <div className="grid md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-7">
            <div className="inline-flex items-center gap-4 bg-gs-ink text-gs-cream px-6 py-3.5 rounded-full mb-10 shadow-md border border-gs-ember/30">
              <img src="/brand/monogram.png" alt="Green Screen" className="h-8 w-auto" />
              <span className="h-5 w-px bg-gs-ember/40" />
              <img src="/brand/wordmark.png" alt="Green Screen Pictures" className="h-5 w-auto" />
              <span className="font-mono text-[10px] uppercase tracking-[0.35em] text-gs-ember ml-1 hidden md:inline">
                {scope.isFilmmakerMode && scope.filmmaker ? `presents · ${scope.filmmaker.name}` : "presents"}
              </span>
            </div>
            <p data-testid="hero-eyebrow" className="font-mono text-[11px] uppercase tracking-[0.3em] text-gs-ember mb-6">
              {scope.isFilmmakerMode && scope.filmmaker
                ? `The films of ${scope.filmmaker.name} · A theatrical home`
                : "Curated indie cinema · One screen at a time"}
            </p>
            {scope.isFilmmakerMode && scope.filmmaker ? (
              <>
                <h1 data-testid="hero-title" className="font-serif font-medium text-5xl sm:text-6xl lg:text-7xl leading-[1.02] text-balance text-gs-ink">
                  {scope.filmmaker.name.split(" ")[0]}<span className="italic text-gs-ember">'s theater.</span>
                </h1>
                <p className="font-sans text-lg text-gs-ink/70 mt-8 max-w-xl leading-relaxed">
                  {scope.filmmaker.bio}
                </p>
              </>
            ) : (
              <>
                <h1 data-testid="hero-title" className="font-serif font-medium text-5xl sm:text-6xl lg:text-7xl leading-[1.02] text-balance text-gs-ink">
                  The theater is small.<br />
                  <span className="italic text-gold-foil">The films are everything.</span>
                </h1>
                <p className="font-sans text-lg text-gs-ink/70 mt-8 max-w-xl leading-relaxed">
                  Green Screen is a single-screen cinema on the internet. We release one or two films at a time and give each its own theatrical home — its mini-site, its night, its audience.
                </p>
              </>
            )}
            <div className="flex flex-wrap gap-4 mt-10">
              {nowShowing[0] && (
                <Link
                  to={`/film/${nowShowing[0].slug}`}
                  data-testid="hero-cta-primary"
                  className="inline-flex items-center gap-3 bg-gs-ink text-gs-cream rounded-none px-9 py-4 font-mono uppercase tracking-[0.25em] text-xs border border-gs-ember/60 hover:bg-gs-ember hover:text-gs-ink transition-all"
                >
                  <Ticket size={18} /> Step inside · {nowShowing[0].title}
                </Link>
              )}
              <a
                href="#now-showing"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center gap-2 border border-gs-ink/20 text-gs-ink rounded-full px-8 py-4 font-sans font-medium hover:border-gs-ink transition-colors"
              >
                Browse the marquee <ArrowUpRight size={16} />
              </a>
            </div>
          </div>

          <div className="md:col-span-5 relative">
            <div className="aspect-[3/4] overflow-hidden rounded-md relative bg-gs-ink">
              <img
                src={
                  scope.isFilmmakerMode && scope.filmmaker?.portrait_url
                    ? scope.filmmaker.portrait_url
                    : (nowShowing[0]?.poster_url || "https://images.unsplash.com/photo-1758232589439-f5ec09dc92c2")
                }
                alt={scope.isFilmmakerMode && scope.filmmaker ? scope.filmmaker.name : (nowShowing[0]?.title || "Cinema marquee")}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-gs-ink/90 via-gs-ink/10 to-gs-ink/30" />
              <div className="absolute top-6 left-6 right-6 flex items-start justify-between">
                <img src="/brand/wordmark.png" alt="Green Screen Pictures" className="h-10 md:h-12 w-auto drop-shadow-[0_2px_16px_rgba(0,0,0,0.9)]" />
              </div>
              <div className="absolute bottom-6 left-6 right-6 text-gs-cream">
                <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-gs-ember">
                  {scope.isFilmmakerMode ? "The director" : "Tonight on the screen"}
                </p>
                <p className="font-display text-3xl tracking-[0.04em] mt-2">
                  {scope.isFilmmakerMode && scope.filmmaker ? scope.filmmaker.name : (nowShowing[0]?.title || "—")}
                </p>
              </div>
            </div>
            <div className="absolute -bottom-6 -left-6 bg-gs-ink text-gs-ember rounded-full px-5 py-2 font-mono text-[10px] uppercase tracking-[0.35em] shadow-md border border-gs-ember/40">
              ● live · {nowShowing.length} {nowShowing.length === 1 ? "film" : "films"} playing
            </div>
          </div>
        </div>
      </section>

      <Marquee
        items={
          scope.isFilmmakerMode && scope.filmmaker
            ? [
                `${scope.filmmaker.name}`,
                "On Green Screen Pictures",
                `${nowShowing.length + comingSoon.length} films in the catalog`,
                "Rent · Buy · Keep",
                "A theatrical home for every film",
              ]
            : [
                "Green Screen Pictures",
                "Curated Indie Cinema",
                "One screen, one story",
                "Rent · Buy · Keep",
                "Made by filmmakers, for filmmakers",
              ]
        }
      />

      {/* NOW SHOWING */}
      <section id="now-showing" className="max-w-7xl mx-auto px-6 md:px-12 py-24">
        <div className="flex items-end justify-between mb-12">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gs-dust">Marquee</p>
            <h2 className="font-serif text-4xl md:text-5xl text-gs-ink mt-2">Now Showing</h2>
          </div>
          <p className="font-sans text-sm text-gs-ink/60 max-w-sm hidden md:block">
            We screen one or two films at a time. Each gets its own room. Click a poster to enter the theater.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-10 md:gap-14">
          {nowShowing.map((f, i) => (
            <Link
              key={f.id}
              to={`/film/${f.slug}`}
              data-testid={`now-showing-card-${i}`}
              className="group block"
            >
              {/* Poster frame — black letterbox so portrait + landscape posters both display fully */}
              <div className="aspect-[16/10] bg-gs-ink overflow-hidden flex items-center justify-center relative border border-gs-line">
                <img
                  src={f.poster_url}
                  alt={f.title}
                  className="max-h-full max-w-full object-contain transition-transform duration-700 group-hover:scale-[1.02]"
                />
                {f.run_status?.is_running && (
                  <div className="absolute top-4 left-4 bg-gs-ink/90 border border-gs-ember/40 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.3em] text-gs-ember">
                    Week {f.run_status.week_index} of {f.run_status.weeks}
                  </div>
                )}
              </div>
              {/* Info plate */}
              <div className="pt-8">
                <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-gs-ember">
                  {f.genre} · {f.runtime_minutes} min · {f.rating}
                </p>
                <p className="font-display text-4xl md:text-5xl tracking-[0.04em] text-gs-ink mt-3">{f.title}</p>
                <p className="font-serif italic text-lg text-gs-ink/70 mt-2">{f.tagline}</p>
                {f.run_status?.is_running && (
                  <p data-testid={`run-week-${i}`} className="font-mono text-[10px] uppercase tracking-[0.3em] text-gs-dust mt-4">
                    {f.run_status.days_remaining} days remaining in this theatrical run
                  </p>
                )}
                <div className="flex items-center gap-3 mt-6">
                  <span className="inline-flex items-center gap-2 bg-gs-ink text-gs-cream rounded-full px-5 py-2.5 text-[10px] font-mono uppercase tracking-[0.3em] border border-gs-ember/50 group-hover:bg-gs-ember group-hover:text-gs-ink transition-colors">
                    <Ticket size={12} /> Buy ${f.buy_price.toFixed(2)}
                  </span>
                  <span className="inline-flex items-center gap-2 border border-gs-ink/20 text-gs-ink rounded-full px-5 py-2.5 text-[10px] font-mono uppercase tracking-[0.3em]">
                    <Clapperboard size={12} /> Rent ${f.rent_price.toFixed(2)}
                  </span>
                </div>
              </div>
            </Link>
          ))}
          {nowShowing.length === 0 && (
            <div className="col-span-2 text-center py-24 border border-dashed border-gs-line rounded-md">
              <p className="font-serif text-2xl text-gs-ink/60">The screen is dark tonight.</p>
            </div>
          )}
        </div>
      </section>

      {/* DIRECTOR */}
      <section className="bg-gs-paper border-y border-gs-line py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-5">
            <img
              src="https://images.unsplash.com/photo-1674507887380-f82e9a1eae1a"
              alt="Filmmaker"
              className="w-full rounded-md aspect-[4/5] object-cover"
            />
          </div>
          <div className="md:col-span-7">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gs-ember">A filmmaker's home</p>
            <h2 className="font-serif text-4xl md:text-5xl text-gs-ink mt-3 leading-tight text-balance">
              Every film I make<br />deserves its own marquee.
            </h2>
            <p className="font-sans text-lg text-gs-ink/70 mt-8 leading-relaxed max-w-xl">
              I built Green Screen because streaming flattens cinema into thumbnails. Here, every film I release gets its own theater — its own night, its own poster, its own audience walking in.
            </p>
            <Link
              to="/about"
              data-testid="director-link"
              className="inline-flex items-center gap-2 mt-8 text-gs-ember font-medium hover:gap-3 transition-all"
            >
              Read the director's note <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* COMING SOON */}
      <section id="coming-soon" className="max-w-7xl mx-auto px-6 md:px-12 py-24">
        <div className="flex items-end justify-between mb-12">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gs-dust">Lineup</p>
            <h2 className="font-serif text-4xl md:text-5xl text-gs-ink mt-2">Coming Soon</h2>
          </div>
        </div>
        {comingSoon.length === 0 ? (
          <p className="font-serif text-xl text-gs-ink/60 italic">Next title is being mixed in the projection booth.</p>
        ) : (
          <div className="grid md:grid-cols-3 gap-8">
            {comingSoon.map((f, i) => (
              <div key={f.id} data-testid={`coming-soon-card-${i}`} className="group">
                <div className="aspect-[3/4] bg-gs-ink overflow-hidden flex items-center justify-center border border-gs-line">
                  <img src={f.poster_url} alt={f.title} className="max-h-full max-w-full object-contain group-hover:scale-[1.02] transition-transform duration-700" />
                </div>
                <div className="pt-6">
                  <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-gs-ember">{f.release_date}</p>
                  <p className="font-display text-3xl mt-2 text-gs-ink tracking-[0.03em]">{f.title}</p>
                  <p className="font-serif italic text-gs-ink/70 mt-1">{f.tagline}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-gs-ink text-gs-cream py-28 relative overflow-hidden">
        <div className="absolute inset-x-0 top-0 divider-gold" />
        <div className="absolute inset-x-0 bottom-0 divider-gold" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
          <div className="text-center mb-20">
            <img src="/brand/monogram.png" alt="" className="h-24 md:h-28 w-auto mx-auto mb-8" />
            <p className="font-mono text-[10px] uppercase tracking-[0.5em] text-gs-ember">An evening at Green Screen</p>
          </div>
          <div className="grid md:grid-cols-3 gap-16">
            {[
              { icon: Film, title: "Choose a film", body: "Each release lives at its own theatrical home — a single-film mini-site built like a real premiere." },
              { icon: Ticket, title: "Rent or buy", body: "Rent for 48 hours of unlimited replays. Buy and keep it in your library forever." },
              { icon: Clapperboard, title: "Watch like opening night", body: "Dim the lights. The film opens the moment your ticket clears the box office." },
            ].map(({ icon: Icon, title, body }) => (
              <div key={title} data-testid={`how-it-works-${title.toLowerCase().replace(/\s+/g, "-")}`} className="text-center md:text-left">
                <Icon size={26} className="text-gs-ember mb-6 mx-auto md:mx-0" />
                <p className="font-display text-3xl tracking-[0.05em] mb-4 text-gs-cream">{title}</p>
                <p className="font-serif text-base text-gs-cream/70 leading-relaxed max-w-xs mx-auto md:mx-0">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <MainFooter />
    </div>
  );
}
